<script setup>
import { computed, onBeforeUnmount, ref, watch, nextTick } from 'vue';
import parsePhoneNumber from 'libphonenumber-js';
import { isValidPhoneNumber } from 'libphonenumber-js';
import countries from 'shared/constants/countries.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages.js';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { DuplicateContactException } from 'shared/helpers/CustomErrors';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactInfoRow from './contact/ContactInfoRow.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const currentChat = useMapGetter('getSelectedChat');
const contactGetter = useMapGetter('contacts/getContact');
const channelType = computed(() => currentChat.value.meta?.channel || '');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value) || {});
const contactAdditionalAttributes = computed(
  () =>
    contact.value.additional_attributes ||
    contact.value.additionalAttributes ||
    {}
);

const phoneFromContact = computed(
  () => contact.value.phone_number || contact.value.phoneNumber || ''
);

const displayPhoneLabel = computed(() => {
  const phone = phoneFromContact.value;
  if (!phone) return '';
  try {
    return parsePhoneNumber(phone).formatInternational();
  } catch {
    return phone;
  }
});

const isPhoneEditing = ref(false);
const phoneDraft = ref('');
const phoneEditContainerRef = ref(null);
let phoneFocusOutTimer;

const countryManuallyEdited = ref(false);

const getCountryMetaFromPhone = phone => {
  if (!phone || typeof phone !== 'string') return null;
  const trimmed = phone.trim();
  if (!trimmed) return null;
  try {
    const parsed = parsePhoneNumber(trimmed);
    if (!parsed?.country) return null;
    const c = countries.find(x => x.id === parsed.country);
    return {
      country_code: parsed.country,
      country: c?.name || parsed.country,
    };
  } catch {
    return null;
  }
};

const countryOptions = computed(() =>
  countries.map(({ name, id, emoji }) => ({
    label: emoji ? `${emoji} ${name}` : name,
    value: id,
  }))
);

const languageOptions = computed(() =>
  languages.map(({ name, id }) => ({ label: name, value: id }))
);

const selectedCountryCode = computed(() => {
  const attrs = contactAdditionalAttributes.value;
  const code = attrs.country_code || attrs.countryCode;
  if (code) return code;
  const { country } = attrs;
  if (country) {
    const match = countries.find(
      c => c.name === country || c.id === country || c.name === String(country)
    );
    if (match) return match.id;
  }
  if (!countryManuallyEdited.value) {
    return getCountryMetaFromPhone(phoneFromContact.value)?.country_code || '';
  }
  return '';
});

const selectedLanguageCode = computed(() => {
  const attrs = contactAdditionalAttributes.value;
  const raw = (attrs.language || attrs.browserLanguage || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (languages.some(l => l.id === lower)) return lower;
  const exactId = languages.find(l => l.id === raw);
  if (exactId) return exactId.id;
  const firstSeg = raw.split(/[-_]/)[0]?.toLowerCase() || '';
  if (firstSeg && languages.some(l => l.id === firstSeg)) return firstSeg;
  const byName = languages.find(
    l => l.name === raw || l.name.toLowerCase() === lower
  );
  return byName?.id || '';
});

const getContactDetails = async () => {
  if (contactId.value) {
    await store.dispatch('contacts/show', { id: contactId.value });
    await store.dispatch('contacts/fetchContactableInbox', contactId.value);
  }
};

const syncConversationSender = () => {
  const chatId = currentChat.value?.id;
  const sender = store.getters['contacts/getContact'](contactId.value);
  if (!chatId || !sender?.id) {
    return;
  }
  store.dispatch('updateConversationContact', {
    conversationId: chatId,
    ...sender,
  });
};

const updateContactField = async attrs => {
  if (!contactId.value) {
    return;
  }

  const payload = {
    id: contactId.value,
    ...attrs,
  };

  if (
    Object.prototype.hasOwnProperty.call(payload, 'phone_number') &&
    !countryManuallyEdited.value
  ) {
    const derived = getCountryMetaFromPhone(payload.phone_number || '');
    if (derived) {
      payload.additional_attributes = {
        ...contactAdditionalAttributes.value,
        ...derived,
      };
    }
  }

  await store.dispatch('contacts/update', payload);
  await getContactDetails();
  syncConversationSender();
};

watch(
  contactId,
  (newContactId, prevContactId) => {
    countryManuallyEdited.value = false;
    isPhoneEditing.value = false;
    if (newContactId && newContactId !== prevContactId) {
      getContactDetails();
    }
  },
  { immediate: true }
);

const openPhoneEdit = () => {
  phoneDraft.value = phoneFromContact.value || '';
  isPhoneEditing.value = true;
  nextTick(() => {
    phoneEditContainerRef.value?.querySelector?.('input')?.focus();
  });
};

const cancelPhoneEdit = () => {
  clearTimeout(phoneFocusOutTimer);
  isPhoneEditing.value = false;
  phoneDraft.value = phoneFromContact.value || '';
};

const savePhoneFromEditor = async () => {
  clearTimeout(phoneFocusOutTimer);
  if (!isPhoneEditing.value) return;

  const raw = phoneDraft.value != null ? String(phoneDraft.value).trim() : '';
  const current = String(phoneFromContact.value || '').trim();

  if (raw === current) {
    isPhoneEditing.value = false;
    return;
  }

  if (raw !== '' && !isValidPhoneNumber(raw)) {
    useAlert(t('PHONE_INPUT.ERROR'));
    nextTick(() => {
      phoneEditContainerRef.value?.querySelector?.('input')?.focus();
    });
    return;
  }

  try {
    await updateContactField({ phone_number: raw });
    isPhoneEditing.value = false;
  } catch (err) {
    const attrs = err instanceof DuplicateContactException ? err.data : null;
    if (Array.isArray(attrs) && attrs.includes('phone_number')) {
      useAlert(t('CONTACT_PANEL.PHONE_NUMBER_IN_USE'));
    } else {
      useAlert(t('CONTACT_FORM.ERROR_MESSAGE'));
    }
    nextTick(() => {
      phoneEditContainerRef.value?.querySelector?.('input')?.focus();
    });
  }
};

const schedulePhoneSaveOnFocusOut = () => {
  clearTimeout(phoneFocusOutTimer);
  phoneFocusOutTimer = setTimeout(() => {
    if (!isPhoneEditing.value) return;
    const el = phoneEditContainerRef.value;
    if (el && el.contains(document.activeElement)) return;
    savePhoneFromEditor();
  }, 120);
};

const onCopyPhone = async e => {
  e.preventDefault();
  e.stopPropagation();
  const v = phoneFromContact.value;
  if (!v) return;
  await copyTextToClipboard(v);
  useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
};

const onCountryComboChange = async countryCode => {
  countryManuallyEdited.value = true;
  const selected = countries.find(c => c.id === countryCode);
  try {
    await updateContactField({
      additional_attributes: {
        ...contactAdditionalAttributes.value,
        country: selected?.name || '',
        country_code: countryCode || '',
      },
    });
  } catch {
    useAlert(t('CONTACT_FORM.ERROR_MESSAGE'));
  }
};

const onLanguageComboChange = async langId => {
  const selected = languages.find(l => l.id === langId);
  try {
    await updateContactField({
      additional_attributes: {
        ...contactAdditionalAttributes.value,
        language: selected?.name || '',
      },
    });
  } catch {
    useAlert(t('CONTACT_FORM.ERROR_MESSAGE'));
  }
};

onBeforeUnmount(() => {
  clearTimeout(phoneFocusOutTimer);
});
</script>

<template>
  <div class="flex h-full w-full flex-col">
    <ContactInfo
      :contact="contact"
      :channel-type="channelType"
      @update-name="value => updateContactField({ name: value })"
    />
    <div class="flex min-h-0 flex-1 flex-col px-3 pb-5 pt-2">
      <section class="w-full flex-1 space-y-2">
        <h3 class="text-sm font-bold text-n-slate-12">
          {{ $t('CONTACT_PANEL.CONTACT_FIELDS') }}
        </h3>

        <div class="w-full space-y-2">
          <div class="space-y-1">
            <div
              class="flex items-center gap-1 text-xs font-medium text-n-slate-11"
            >
              <span>{{ $t('CONTACT_PANEL.PHONE_NUMBER') }}</span>
              <span class="i-lucide-info size-3 text-n-slate-10" />
            </div>
            <div
              v-if="isPhoneEditing"
              ref="phoneEditContainerRef"
              tabindex="-1"
              class="outline-none"
              @keydown.esc.prevent="cancelPhoneEdit"
              @focusout="schedulePhoneSaveOnFocusOut"
            >
              <PhoneNumberInput
                v-model="phoneDraft"
                :placeholder="
                  $t('CONTACTS_LAYOUT.NEW_CONTACT_PANEL.PHONE_NUMBER')
                "
                :country-hint="
                  contactAdditionalAttributes.countryCode ||
                  contactAdditionalAttributes.country_code ||
                  ''
                "
              />
            </div>
            <div v-else class="group/phone w-full min-h-5 ltr:-ml-1 rtl:-mr-1">
              <div
                role="button"
                tabindex="0"
                class="flex cursor-pointer items-center gap-2 text-n-slate-11 hover:text-n-slate-12"
                @click="openPhoneEdit"
                @keydown.enter.prevent="openPhoneEdit"
              >
                <span
                  class="i-lucide-phone size-3.5 shrink-0 text-n-slate-11 ltr:ml-1 rtl:mr-1"
                  aria-hidden="true"
                />
                <span
                  v-if="displayPhoneLabel"
                  class="overflow-hidden text-sm whitespace-nowrap text-ellipsis"
                  :title="displayPhoneLabel"
                >
                  {{ displayPhoneLabel }}
                </span>
                <span v-else class="text-sm text-n-slate-11">
                  {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
                </span>
                <NextButton
                  v-if="phoneFromContact"
                  ghost
                  xs
                  slate
                  class="ltr:-ml-1 rtl:-mr-1"
                  icon="i-lucide-clipboard"
                  @click="onCopyPhone"
                />
                <NextButton
                  ghost
                  xs
                  slate
                  class="ltr:-ml-1 rtl:-mr-1 opacity-0 transition-opacity group-hover/phone:opacity-100"
                  icon="i-lucide-pencil"
                  @click.stop.prevent="openPhoneEdit"
                />
              </div>
            </div>
          </div>

          <div class="space-y-1">
            <div
              class="flex items-center gap-1 text-xs font-medium text-n-slate-11"
            >
              <span>{{ $t('CONTACT_PANEL.EMAIL_ADDRESS') }}</span>
              <span class="i-lucide-info size-3 text-n-slate-10" />
            </div>
            <ContactInfoRow
              :href="contact.email ? `mailto:${contact.email}` : ''"
              :value="contact.email"
              icon="mail"
              emoji="✉️"
              :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
              show-copy
              editable
              @update="value => updateContactField({ email: value })"
            />
          </div>

          <div class="space-y-1">
            <div
              class="flex items-center gap-1 text-xs font-medium text-n-slate-11"
            >
              <span>{{ $t('CONTACTS_LAYOUT.TABLE.COUNTRY') }}</span>
              <span class="i-lucide-info size-3 text-n-slate-10" />
            </div>
            <ComboBox
              :model-value="selectedCountryCode"
              :options="countryOptions"
              :placeholder="$t('CONTACT_PANEL.COUNTRY_PLACEHOLDER')"
              :search-placeholder="
                $t('CONTACT_FORM.FORM.COUNTRY.SELECT_PLACEHOLDER')
              "
              class="w-full [&>div>button]:min-h-9 [&>div>button]:py-2"
              @update:model-value="onCountryComboChange"
            />
          </div>

          <div class="space-y-1">
            <div
              class="flex items-center gap-1 text-xs font-medium text-n-slate-11"
            >
              <span>{{ $t('CONTACTS_LAYOUT.TABLE.LANGUAGE') }}</span>
              <span class="i-lucide-info size-3 text-n-slate-10" />
            </div>
            <ComboBox
              :model-value="selectedLanguageCode"
              :options="languageOptions"
              :placeholder="$t('CONTACT_PANEL.LANGUAGE_PLACEHOLDER')"
              :search-placeholder="$t('COMBOBOX.SEARCH_PLACEHOLDER')"
              class="w-full [&>div>button]:min-h-9 [&>div>button]:py-2"
              @update:model-value="onLanguageComboChange"
            />
          </div>
        </div>
      </section>

      <section
        class="-mx-3 mt-4 w-auto shrink-0 border-t border-n-weak px-3 pt-3"
      >
        <ContactLabels :contact-id="contactId" />
      </section>
    </div>
  </div>
</template>
